import { setup, assign } from "xstate"
import type { UsageProfile } from "@modules/usage-profile/types"

// ================================
// TYPES
// ================================

export interface UsageProfileFactoryContext {
    fromState: string
    profile: UsageProfile | { name: string } | null
    modify: boolean
}

export type UsageProfileFactoryEvent =
    | { type: "REDIRECT" }
    | { type: "CLOSE" }
    | { type: "EDIT" }
    | { type: "REPLACE" }
    | { type: "UPDATE_FROM_STATE"; value: string }
    | { type: "UPDATE_PROFILE"; value: UsageProfile | { name: string } | null }

// ================================
// MACHINE
// ================================

export function createUsageProfileFactoryMachine(params: {
    fromState: string
    profile: UsageProfile | { name: string } | null
    modify: boolean
    onCloseDialog: () => void
}) {
    const { fromState, profile, modify, onCloseDialog } = params

    return setup({
        types: {
            context: {} as UsageProfileFactoryContext,
            events: {} as UsageProfileFactoryEvent,
        },
        actions: {
            closeDialog: () => onCloseDialog(),
            updateProfile: assign({
                profile: ({ event }) =>
                    event.type === "UPDATE_PROFILE" ? event.value : null,
            }),
            resetContext: assign({
                profile: modify ? profile : null,
                fromState: fromState,
            }),
            initNewProfile: assign({
                profile: () => ({ name: "Sans titre" }),
            }),
        },
        guards: {
            isEdit: ({ context }) => context.fromState === "edit",
            isReplace: ({ context }) => context.fromState === "replace",
        },
    }).createMachine({
        id: "UsageProfileFactory",
        initial: "redirect",
        context: {
            fromState,
            profile,
            modify,
        },
        states: {
            redirect: {
                on: {
                    UPDATE_PROFILE: {
                        actions: "updateProfile",
                    },
                    UPDATE_FROM_STATE: {
                        actions: assign({
                            fromState: ({ event }) =>
                                event.type === "UPDATE_FROM_STATE"
                                    ? event.value
                                    : "",
                        }),
                    },
                    REDIRECT: [
                        {
                            target: "UsageProfileEdit",
                            guard: "isEdit",
                        },
                        {
                            target: "UsageProfileExist",
                            guard: "isReplace",
                        },
                        {
                            target: "UsageProfileOptions",
                        },
                    ],
                },
            },
            UsageProfileOptions: {
                on: {
                    CLOSE: {
                        target: "redirect",
                        actions: ["closeDialog", "resetContext"],
                    },
                    EDIT: {
                        target: "UsageProfileEdit",
                        actions: "initNewProfile",
                    },
                    REPLACE: {
                        target: "UsageProfileExist",
                        actions: "resetContext",
                    },
                },
            },
            UsageProfileEdit: {
                on: {
                    CLOSE: {
                        target: "redirect",
                        actions: ["closeDialog", "resetContext"],
                    },
                    REPLACE: {
                        target: "UsageProfileExist",
                        actions: "resetContext",
                    },
                },
            },
            UsageProfileExist: {
                on: {
                    CLOSE: {
                        target: "redirect",
                        actions: ["closeDialog", "resetContext"],
                    },
                    EDIT: {
                        target: "UsageProfileEdit",
                        actions: "initNewProfile",
                    },
                },
            },
        },
    })
}
